import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, api, qs } from "../lib/api.ts";
import { useApi, relativeTime, titleCase } from "../lib/hooks.ts";
import { useAuth } from "../lib/auth.tsx";
import type {
  AgentUpdateResult, AgentVersionInfo, DeviceGroup, Paged, Device,
} from "../lib/types.ts";
import { Badge, Card, Empty, ErrorAlert, Pager, Spinner } from "../components/ui.tsx";

const COLUMNS: Array<{ key: string; label: string }> = [
  { key: "name", label: "Dispositivo" },
  { key: "user", label: "Usuario" },
  { key: "platform", label: "Plataforma" },
  { key: "status", label: "Status" },
  { key: "lastSeen", label: "Ultimo contato" },
  { key: "riskScore", label: "Risco" },
];

const AGENT_BADGE: Record<string, { value: string; label: string }> = {
  current: { value: "completed", label: "Atualizado" },
  outdated: { value: "warning", label: "Desatualizado" },
  unknown: { value: "unknown", label: "Sem versao" },
};

function AgentBadge({ state }: { state: string }) {
  const badge = AGENT_BADGE[state] ?? { value: "unknown", label: "Sem versao" };
  return <Badge value={badge.value} label={badge.label} />;
}

const STATUSES = ["online", "offline", "missing", "frozen", "wiped", "retired"];
const PLATFORMS = ["Windows", "macOS", "Linux", "ChromeOS"];

export function DevicesPage() {
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState(params.get("q") ?? "");

  const q = params.get("q") ?? "";
  const status = params.get("status") ?? "";
  const platform = params.get("platform") ?? "";
  const groupId = params.get("groupId") ?? "";
  const sort = params.get("sort") ?? "name";
  const dir = params.get("dir") ?? "asc";
  const page = Number(params.get("page") ?? 1);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (draft !== q) update({ q: draft, page: 1 });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  function update(patch: Record<string, string | number | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "" || value === undefined) next.delete(key);
      else next.set(key, String(value));
    }
    setParams(next, { replace: true });
  }

  const path = useMemo(
    () =>
      `/api/v3/devices${qs({ q, status, platform, groupId, sort, dir, page, limit: 25 })}`,
    [q, status, platform, groupId, sort, dir, page],
  );

  const devices = useApi<Paged<Device>>(path, [], { poll: 5000 });
  const groups = useApi<{ data: DeviceGroup[] }>("/api/v3/device-groups");
  const agentInfo = useApi<AgentVersionInfo>("/api/v3/agents/version", [], { poll: 30000 });
  const { can } = useAuth();
  const [agentBusy, setAgentBusy] = useState(false);
  const [agentMsg, setAgentMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const total = devices.data?.meta.total ?? 0;
  const agentCounts = agentInfo.data?.counts;
  const agentTarget = agentInfo.data?.version ?? "";
  const agentStates = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of agentInfo.data?.devices ?? []) map.set(item.uid, item.state);
    return map;
  }, [agentInfo.data]);

  async function updateAllAgents() {
    if (!agentCounts || !agentTarget) return;
    const pending = agentCounts.outdated;
    if (pending <= 0) return;
    const offline = agentCounts.offlineOutdated;
    const extra = offline > 0 ? ` ${offline} offline receberao a atualizacao quando voltarem.` : "";
    if (!window.confirm(
      `Enviar a atualizacao do agente ${agentTarget} para ${pending} computador(es)?${extra}`,
    )) return;
    setAgentBusy(true);
    setAgentMsg(null);
    try {
      const res = await api<AgentUpdateResult>("/api/v3/agents/update", { method: "POST" });
      const parts = [`Atualizacao ${res.version}: ${res.queued} computador(es) com atualizacao imediata enfileirada.`];
      if (res.autoUpdate > 0) {
        parts.push(`${res.autoUpdate} agente(s) em versao antiga serao atualizados automaticamente na proxima checagem (ate 3 min).`);
      }
      if (res.alreadyCurrent > 0) parts.push(`${res.alreadyCurrent} ja estavam na versao publicada.`);
      setAgentMsg({ kind: "ok", text: parts.join(" ") });
      agentInfo.reload();
      devices.reload();
    } catch (err) {
      setAgentMsg({
        kind: "err",
        text: err instanceof ApiError ? err.message : "Falha ao solicitar atualizacao dos agentes",
      });
    } finally {
      setAgentBusy(false);
    }
  }

  function toggleSort(key: string) {
    if (sort === key) update({ dir: dir === "asc" ? "desc" : "asc", page: 1 });
    else update({ sort: key, dir: "asc", page: 1 });
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Dispositivos</h1>
          <p className="subtle">
            {devices.loading ? "Carregando inventario..." : `${total} dispositivos no inventario`}
          </p>
          {agentCounts && agentTarget ? (
            <p className="subtle">
              Agente publicado: <strong className="mono">v{agentTarget}</strong> ·{" "}
              {agentCounts.current} de {agentCounts.total} atualizados
              {agentCounts.outdated > 0 ? ` · ${agentCounts.outdated} desatualizado(s)` : ""}
              {agentCounts.offlineOutdated > 0 ? ` (${agentCounts.offlineOutdated} offline)` : ""}
            </p>
          ) : null}
        </div>
        <span className="spacer" />
        {can("actions.issue") ? (
          <button
            className="btn primary"
            onClick={updateAllAgents}
            disabled={agentBusy || !agentCounts || agentCounts.outdated <= 0 || !agentTarget}
            title={
              agentCounts && agentCounts.outdated > 0
                ? `Enviar a versao ${agentTarget} para os agentes desatualizados`
                : "Todos os agentes ja estao na versao publicada"
            }
          >
            {agentBusy ? "Solicitando..." : "Atualizar agentes"}
          </button>
        ) : null}
        <a
          className="btn"
          href="/SecureEndpoint-Setup.exe"
          download
          title={agentTarget ? `Baixar o instalador (.exe) do agente ${agentTarget}` : "Baixar o instalador (.exe) do agente"}
        >
          Baixar instalador
        </a>
        <button className="btn" onClick={() => devices.reload()}>
          Atualizar
        </button>
      </div>

      {agentMsg ? (
        <div className={`alert ${agentMsg.kind === "ok" ? "info" : "danger"}`}>{agentMsg.text}</div>
      ) : null}

      <Card>
        <div className="toolbar">
          <div className="grow">
            <input
              className="input"
              placeholder="Buscar por nome, host, serie ou usuario..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Busca"
            />
          </div>
          <select className="select" style={{ width: 160 }} value={status} onChange={(e) => update({ status: e.target.value, page: 1 })} aria-label="Status">
            <option value="">Todos os status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </select>
          <select className="select" style={{ width: 160 }} value={platform} onChange={(e) => update({ platform: e.target.value, page: 1 })} aria-label="Plataforma">
            <option value="">Todas plataformas</option>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select className="select" style={{ width: 220 }} value={groupId} onChange={(e) => update({ groupId: e.target.value, page: 1 })} aria-label="Grupo">
            <option value="">Todos os grupos</option>
            {(groups.data?.data ?? []).map((g) => (
              <option key={g.id} value={String(g.id)}>
                {g.name} ({g.deviceCount})
              </option>
            ))}
          </select>
          {q || status || platform || groupId ? (
            <button
              className="btn ghost"
              onClick={() => {
                setDraft("");
                setParams(new URLSearchParams(), { replace: true });
              }}
            >
              Limpar filtros
            </button>
          ) : null}
        </div>
      </Card>

      <Card tight>
        {devices.error ? <div style={{ padding: 16 }}><ErrorAlert message={devices.error.message} /></div> : null}
        {devices.loading && !devices.data ? (
          <div className="empty"><Spinner /></div>
        ) : !devices.data || devices.data.data.length === 0 ? (
          <Empty>Nenhum dispositivo corresponde aos filtros.</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="sortable"
                        onClick={() => toggleSort(col.key)}
                        title="Ordenar"
                      >
                        {col.label}
                        {sort === col.key ? (dir === "asc" ? " ▲" : " ▼") : ""}
                      </th>
                    ))}
                    <th>Agente</th>
                    <th>Politica</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.data.data.map((d) => (
                    <tr key={d.uid}>
                      <td>
                        <Link to={`/devices/${d.uid}`} className="cell-title">
                          {d.name}
                        </Link>
                        {d.fenceState === "outside" ? (
                          <span className="badge failed" style={{ marginLeft: 8 }} title="Fora da cerca virtual">
                            FORA DA CERCA
                          </span>
                        ) : null}
                        <div className="cell-sub">
                          {d.make} {d.model} · {d.serial}
                        </div>
                      </td>
                      <td>
                        <div>{d.user.name}</div>
                        <div className="cell-sub">{d.user.department}</div>
                      </td>
                      <td>
                        <div>{d.platform}</div>
                        <div className="cell-sub">{d.osVersion}</div>
                      </td>
                      <td>
                        <Badge value={d.status} />
                      </td>
                      <td className="cell-sub">{relativeTime(d.lastSeen)}</td>
                      <td>
                        <strong style={{ color: d.riskScore >= 70 ? "var(--danger)" : undefined }}>
                          {d.riskScore}
                        </strong>
                      </td>
                      <td>
                        <div className="mono">{d.agentVersion || "—"}</div>
                        <AgentBadge state={agentStates.get(d.uid) ?? "unknown"} />
                      </td>
                      <td className="cell-sub">{d.group?.name ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager meta={devices.data.meta} page={page} onPage={(next) => update({ page: next })} />
          </>
        )}
      </Card>
    </div>
  );
}
